import React, { useState, useMemo, useEffect } from "react";
import { get, isEmpty } from "lodash-es";
import axios from "axios";
import { apiUrl } from "features/configure";
import { useForm, Controller, useFormState } from "react-hook-form";

import Box from "@mui/material/Box";
import { connect, useDispatch, useSelector } from "react-redux";
import { useOutletContext } from "react-router-dom";

import { InputField } from "@metacrm/metacrm-material-ui/dist/InputField";

import FixedButton from "features/metadesk/settings/common/FixedButton";
import { useConnectWallet as useGlobalReduxConnectWallet } from "features/home/redux/hooks";
import { useFetchEntityConfig } from "features/metadesk/redux/hooks";
import { useIntl } from "react-intl";
import {
  StyledProfileContainer,
  StyledRowItem,
  StyledRowTitleContainer,
  StyledRowTitle,
  StyledRowInputContainer,
  StyledPersonalProfileContainer,
  StyledTitleDescription,
} from "./Profile.styles";
import { StyledWhiteContainer } from "features/metadesk/settings/common.styles";
import { useTheme } from "@mui/styles";
import { Loading } from "features/common";
import _ from "lodash";
import Grid from "@metacrm/metacrm-material-ui/dist/Grid";
import {
  Button,
  VARIANT,
  COLOR,
} from "@metacrm/metacrm-material-ui/dist/Button";
import isEmail from "validator/lib/isEmail";
import useGetPagePermission from "hooks/useGetPagePermission";
import useDispatchSnackbar from "hooks/useDispatchSnackbar";
import { openWindow } from "features/metadesk/utils";
import { useConfirm } from "features/home/ConfirmDialogProvider";
import { alertServerError } from "features/helpers/utils";
import Popup from "@metacrm/metacrm-material-ui/dist/Popup";
import { selectEcosystemName } from "features/metadesk/redux/entityConfig/entityConfig.selector";
import useConnectWallet from "features/metadesk/Onboarding/hook/useConnectWallet";
import { StyledLoginCard } from "components/ConnectWallet/ConnectWallet.styles";

const personalDefaultFormValue = {
  name: "",
  email: "",
  telegram: "",
  address: "",
};

const Profile = () => {
  const formDataInit = {
    ...personalDefaultFormValue,
  };
  const confirm = useConfirm();
  const theme = useTheme();
  const [entityName, isCustomDomain] = useOutletContext();
  const [formData, setFormData] = useState(formDataInit);
  const [isLoading, setIsLoading] = useState(true);
  const { fetchEntityConfig, entityConfig } = useFetchEntityConfig();
  const { formatMessage } = useIntl();
  const { getAuthenticatedInfo } = useGlobalReduxConnectWallet();
  const ecosystem = useSelector(selectEcosystemName);
  const {
    connectorList,
    handleConnectWallet,
    handleDisconnectWallet,
    address,
  } = useConnectWallet({
    ecosystem,
  });
  const [connectWalletPopup, setConnectWalletPopup] = useState(false);

  function updateFormAndConfig(data) {
    reset({
      ...getPersonalData(data),
    });
    setFormData({ ...getPersonalData(data) });
    getAuthenticatedInfo(entityName);
    fetchEntityConfig({ entityName });
    dispatchSnackbar({
      message: "Save successfully",
      variant: "success",
    });
  }

  const saveProfileInfo = async (personal) => {
    try {
      if (!userPermission.canEdit) return;
      const { data } = await axios.post(
        `${apiUrl}/api/manage/profile/${entityName}/personal`,
        {
          personal,
        }
      );
      updateFormAndConfig(data);
    } catch (err) {
      if (err?.response?.data?.error === "Address already used") {
        await handleDisconnectWallet();
      }
      dispatchSnackbar({ err });
    }
  };

  const disconnect = async (type) => {
    try {
      if (!userPermission.canEdit) return;
      const personal = {
        ...formData,
        [type]: null,
      };
      if (type === "address") {
        await handleDisconnectWallet();
      }
      const { data } = await axios.post(
        `${apiUrl}/api/manage/profile/${entityName}/personal`,
        {
          personal,
        }
      );
      updateFormAndConfig(data);
    } catch (err) {
      dispatchSnackbar({ err });
    }
  };

  async function connectWallet(connector) {
    await handleConnectWallet(connector);
  }

  useEffect(() => {
    if (connectWalletPopup && address) {
      async function saveAddress() {
        await saveProfileInfo({ ...formData, address });
        setConnectWalletPopup(false);
      }
      saveAddress();
    }
  }, [connectWalletPopup, address]);

  function connectGoogle() {
    // 開啟一個小視窗
    const authWindow = window.open(
      `${apiUrl}/api/auth/google`, // 後端 Google OAuth 登入 URL
      "_blank",
      "width=500,height=600"
    );

    // 監聽後端回傳的 email
    const handleMessage = async (event) => {
      if (event.origin !== apiUrl) return; // 確保是從後端來的
      const email = event.data?.email;
      const error = event.data?.error;
      if (email) {
        authWindow.close(); // 關閉小視窗
        await saveProfileInfo({ ...formData, email });
      }
      if (error) {
        dispatchSnackbar({ err: error });
      }
      window.removeEventListener("message", handleMessage);
    };

    window.addEventListener("message", handleMessage);
  }
  const dispatch = useDispatch();
  const {
    handleSubmit,
    control,
    formState: { errors: errorsInfo, isValid, isDirty },
    reset,
  } = useForm({
    mode: "onChange",
    defaultValues: formDataInit,
  });

  const getPersonalData = (data) => ({
    address: get(data, "personal.address"),
    email: get(data, "personal.email"),
    name: get(data, "personal.name"),
    telegram: get(data, "personal.telegram"),
    discordHandle: get(data, "personal.discordHandle"),
  });

  const fetchProfile = async () => {
    setIsLoading(true);
    try {
      const { data } = await axios.get(
        `${apiUrl}/api/manage/profile/${entityName}`
      );

      setFormData({ ...getPersonalData(data) });
      reset({ ...getPersonalData(data) });
    } catch (error) {
      dispatchSnackbar({ err: error });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const pagePermission = useGetPagePermission();
  const userPermission = pagePermission("profileUser");
  const dispatchSnackbar = useDispatchSnackbar();

  const handleSave = () => {
    handleSubmit(saveProfileInfo)();
  };

  const handleFieldChange = (event) => {
    const { value, name } = event.target;
    setFormData({ ...formData, [name]: value });
  };

  const renderBindingSection = ({
    platform,
    key,
    value,
    unbindApi,
    bindUrl,
  }) => {
    return (
      <StyledRowInputContainer component={Grid} item xs={7}>
        {value ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              paddingLeft: "12px",
            }}
          >
            @{value}
            <Button
              variant={VARIANT.TEXT}
              color={COLOR.SECONDARY}
              sx={{
                paddingRight: "8px",
                justifyContent: "flex-end",
              }}
              onClick={() => {
                confirm({
                  title: formatMessage({
                    id: `popup.unbind${platform}.title`,
                  }),
                  content: formatMessage({
                    id: `popup.unbind${platform}.description`,
                  }),
                  confirmText: formatMessage({
                    id: "global.btn.unbind",
                  }),
                  cancelText: formatMessage({
                    id: "global.btn.cancel",
                  }),
                })
                  .then(async () => {
                    try {
                      await axios.post(unbindApi);
                      setFormData({
                        ...formData,
                        [key]: "",
                      });
                    } catch (err) {
                      dispatch(alertServerError(err));
                    }
                  })
                  .catch(() => {});
              }}
            >
              {formatMessage({ id: "global.btn.unbind" })}
            </Button>
          </Box>
        ) : (
          <Button
            variant={VARIANT.TEXT}
            color={COLOR.SECONDARY}
            sx={{
              paddingLeft: "8px",
              justifyContent: "flex-start",
              gap: "4px",
            }}
            onClick={() => {
              openWindow(bindUrl, true);
            }}
          >
            <span style={{ fontWeight: 700 }}>
              {formatMessage({ id: "global.btn.bindNow" })}
            </span>
            <i className="meta-crm-icon-ic_arrowForward font-size-16" />
          </Button>
        )}
      </StyledRowInputContainer>
    );
  };

  return (
    <StyledProfileContainer>
      <Box className="title fade-up" mb="20px">
        {formatMessage({ id: "profileSetting.page.title" })}
      </Box>

      <StyledWhiteContainer className="fade-up-more">
        <Loading open={isLoading} fullScreen={false} />

        {userPermission.show && (
          <StyledPersonalProfileContainer>
            <Box
              fontSize={"16px"}
              fontWeight="700"
              color={(theme) => theme.customColors.grey[700]}
            >
              {formatMessage({ id: "profileSetting.personalProfile.title" })}
            </Box>
            <StyledTitleDescription>
              {formatMessage({
                id: "profileSetting.personalProfile.description",
              })}
            </StyledTitleDescription>
            <StyledRowItem
              component={Grid}
              container
              columnSpacing={3}
              alignItems="center"
              style={{ marginTop: userPermission.readonly ? "20px" : 0 }}
            >
              <StyledRowTitleContainer component={Grid} item xs={5}>
                <StyledRowTitle isRequired>
                  {formatMessage({ id: "global.commonWord.name" })}
                </StyledRowTitle>
              </StyledRowTitleContainer>
              <StyledRowInputContainer component={Grid} item xs={7}>
                <InputField
                  readonly={userPermission.readonly}
                  name="name"
                  value={formData.name}
                  control={control}
                  errors={get(errorsInfo, "name.message")}
                  maxLength={25}
                  rules={{
                    required: !userPermission.readonly
                      ? {
                          value: true,
                          message:
                            formatMessage({
                              id: "global.commonWord.name",
                            }) + formatMessage({ id: "global.error.required" }),
                        }
                      : undefined,
                  }}
                  onChange={(e) => handleFieldChange(e)}
                  errorTextAbsolute
                />
              </StyledRowInputContainer>
            </StyledRowItem>

            <StyledRowItem
              component={Grid}
              container
              columnSpacing={3}
              alignItems="center"
            >
              <StyledRowTitleContainer component={Grid} item xs={5}>
                <StyledRowTitle>
                  {formatMessage({ id: "global.commonWord.discord" })}
                </StyledRowTitle>
              </StyledRowTitleContainer>
              {renderBindingSection({
                platform: "Discord",
                key: "discordHandle",
                value: formData.discordHandle,
                unbindApi: `${apiUrl}/api/discord/user/${entityName}/disconnectDiscordUser`,
                bindUrl: entityConfig?.fetchDiscordUserUrl,
              })}
            </StyledRowItem>
            <StyledRowItem
              component={Grid}
              container
              columnSpacing={3}
              alignItems="center"
            >
              <StyledRowTitleContainer component={Grid} item xs={5}>
                <StyledRowTitle>
                  {formatMessage({ id: "global.commonWord.telegram" })}
                </StyledRowTitle>
              </StyledRowTitleContainer>
              <StyledRowInputContainer component={Grid} item xs={7}>
                <InputField
                  name="telegram"
                  readonly={userPermission.readonly}
                  value={formData.telegram}
                  control={control}
                  onChange={(e) => handleFieldChange(e)}
                />
              </StyledRowInputContainer>
            </StyledRowItem>
            <StyledRowItem
              component={Grid}
              container
              columnSpacing={3}
              alignItems="center"
            >
              <StyledRowTitleContainer component={Grid} item xs={5}>
                <StyledRowTitle>
                  {formatMessage({ id: "global.form.email.label" })}
                </StyledRowTitle>
              </StyledRowTitleContainer>
              <StyledRowInputContainer component={Grid} item xs={7}>
                <Box className="item-flex" justifyContent="space-between">
                  {formData.email ? (
                    <>
                      <InputField value={formData.email} readonly={true} />
                      <Button
                        disabled={!formData.address}
                        variant={VARIANT.OUTLINED}
                        onClick={() => disconnect("email")}
                      >
                        {formatMessage({
                          id: "profileSetting.btn.disconnectGoogle",
                        })}
                      </Button>
                    </>
                  ) : (
                    <Button variant={VARIANT.OUTLINED} onClick={connectGoogle}>
                      <img
                        src={require("assets/img/google.png")}
                        width={20}
                        height={20}
                        style={{ marginRight: "4px" }}
                      />
                      {formatMessage({
                        id: "profileSetting.btn.connectGoogle",
                      })}
                    </Button>
                  )}
                </Box>
              </StyledRowInputContainer>
            </StyledRowItem>
            <StyledRowItem
              component={Grid}
              container
              columnSpacing={3}
              alignItems="center"
            >
              <StyledRowTitleContainer component={Grid} item xs={5}>
                <StyledRowTitle>
                  {formatMessage({ id: "global.commonWord.address" })}
                </StyledRowTitle>
              </StyledRowTitleContainer>
              <StyledRowInputContainer component={Grid} item xs={7}>
                <Box className="item-flex" justifyContent="space-between">
                  {formData.address ? (
                    <>
                      <InputField readonly value={formData.address} />
                      <Button
                        disabled={!formData.email}
                        variant={VARIANT.OUTLINED}
                        onClick={() => disconnect("address")}
                      >
                        {formatMessage({
                          id: "profileSetting.btn.disconnectWallet",
                        })}
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant={VARIANT.OUTLINED}
                      onClick={() => setConnectWalletPopup(true)}
                    >
                      {formatMessage({
                        id: "profileSetting.btn.connectWallet",
                      })}
                    </Button>
                  )}
                </Box>
              </StyledRowInputContainer>
            </StyledRowItem>
          </StyledPersonalProfileContainer>
        )}
      </StyledWhiteContainer>
      <Popup
        open={connectWalletPopup}
        onClose={() => setConnectWalletPopup(false)}
        title="Connect with a wallet"
      >
        {connectorList.map((connector) => (
          <StyledLoginCard
            sx={{ width: "100%" }}
            key={connector.name}
            onClick={() => connectWallet(connector)}
          >
            <img
              src={connector.img}
              style={{ width: 36, height: 36, borderRadius: "8px" }}
              alt={`${connector.name} logo`}
            />
            <Box ml="12px">{connector.name}</Box>
          </StyledLoginCard>
        ))}
      </Popup>
      {userPermission.canEdit && (
        <FixedButton>
          <Button
            sx={{ width: "120px", marginLeft: "8px" }}
            onClick={handleSave}
            disabled={!isValid || !isDirty}
            isLoading={isLoading}
          >
            {formatMessage({ id: "global.btn.save" })}
          </Button>
        </FixedButton>
      )}
    </StyledProfileContainer>
  );
};

export default Profile;
